import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import PrimaryButton from "../utils/buttons/primaryButton";
import { CancellationReason } from "./CancellationReason";
import { Dialog } from "@headlessui/react";
import { icons8crossFilled } from "../../assets";
import RescheduleForm from "./RescheduleForm";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { Button, Tooltip } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import axiosInstance, {
  notifySuccess,
  notifyWarning,
} from "../../utility/axiosInstance";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { compareTwoDate, compareTwoDateIFG } from "../../utility/utils";
import { fetchUsedCarsSettingInfo } from "../../redux/usedCarsThunk";
import RescheduleDatePicker from "../Common/componets/RescheduleDatePicker";

export const BookingStepsUpdateCard = ({
  currentStepInfo,
  onUpdate,
  data,
  setData,
  description,
  isCancellation,
  setIsReject,
  isReject,
  setCurrentStepInfo,
  loading,
  buttonLoading,
}) => {
  const { t } = useTranslation();
  const country = useSelector((state) => state?.user.countryList);
  const [current, setCurrent] = useState(0);
  const [cancellationReason, setCancellationReason] = useState(false);
  const [isReschedule, setIsReschedule] = useState(false);
  const [proposedDate, setProposedDate] = useState("");
  const [proposedTime, setProposedTime] = useState("");
  const [availableWorkingDays, setAvailableWorkingDays] = useState([]);
  const { usedCarSettingInfo } = useSelector((state) => state.usedCars);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!usedCarSettingInfo.length > 0) {
      dispatch(fetchUsedCarsSettingInfo({ dispatch }));
    }
  }, []);

  useEffect(() => {
    try {
      if (usedCarSettingInfo?.length > 0) {
        const currentDate = new Date();
        const carId = data?.booking?.car._id;
        if (!carId) {
          return;
        }
        const carSetting = usedCarSettingInfo.find(({ car }) => carId == car);
        if (carSetting) {
          const usedCarMonthSetting = carSetting?.usedCarMonthSetting?.filter(
            ({ monthYearDate }) => {
              const myDate = new Date(monthYearDate);
              const currentMonthYear = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth()
              );
              const monthYearDateObj = new Date(
                myDate.getFullYear(),
                myDate.getMonth()
              );

              return monthYearDateObj >= currentMonthYear;
            }
          );

          const availableDays = [];

          usedCarMonthSetting?.forEach(({ usedCarDateSettings }) => {
            availableDays.push(...usedCarDateSettings);
          });

          if (availableDays.length > 0) {
            const holidays = carSetting?.holidays.map(({ date }) => date);
            setAvailableWorkingDays(
              availableDays.filter(
                ({ date }) =>
                  compareTwoDateIFG(date, currentDate) &&
                  !holidays.includes(date)
              )
            );
          }
          // serviceMonthSetting.workingDetails
        }
      }
    } catch (error) {
      console.log(error);
    }
  }, [usedCarSettingInfo, data]);

  const [isAddRemarks, setIsAddRemarks] = useState({
    isActive: false,
    remarks: "",
    state: "addRemarks",
  });
  const [isPaymentOffline, setIsPaymentOffline] = useState({
    isActive: false,
    data: "",
  });

  const [basePrice, setBasePrice] = useState(data?.booking?.basePrice || "");
  const [debouncedBasePrice, setDebouncedBasePrice] = useState(basePrice);

  const isFetching = useRef(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedBasePrice(basePrice);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [basePrice]);

  // Memoized fetchTotalAmount function
  const fetchTotalAmount = useCallback(
    async (price) => {
      if (!price) return null;

      try {
        const response = await axiosInstance.get(
          `/usedCars/check-total-amount`,
          {
            params: {
              id: data?.booking?._id,
              basePrice: price,
            },
          }
        );

        if (response.data) {
          // notifySuccess("Amount calculated successfully");
          return response.data?.amountToBePaid;
        }
      } catch (error) {
        console.error("Error fetching total amount:", error);
        notifyWarning("Error fetching total amount");
        return null;
      }
    },
    [data?.booking?._id]
  );

  // Effect to call fetchTotalAmount when debouncedBasePrice changes
  useEffect(() => {
    const calculateAmount = async () => {
      if (debouncedBasePrice && !isFetching.current) {
        const amountToBePaid = await fetchTotalAmount(debouncedBasePrice);
        if (amountToBePaid !== null) {
          setIsPaymentOffline((prev) => ({
            ...prev,
            data: {
              ...prev.data,
              amountPaid: amountToBePaid,
              basePrice: debouncedBasePrice,
            },
          }));
        }
      }
    };

    if (debouncedBasePrice !== data?.booking?.basePrice) {
      calculateAmount();
    }
  }, [debouncedBasePrice, fetchTotalAmount, data?.booking?.basePrice]);

  // Handle base price change
  const handleBasePriceChange = (e) => {
    setBasePrice(e.target.value);
  };

  useEffect(() => {
    setCurrent(currentStepInfo);
  }, [currentStepInfo, data]);

  const handleUpdate = (status) => {
    onUpdate(status, { _id: data._id, ...status });
  };

  useEffect(() => {
    const remarks = data?.booking?.statusHistory?.find(
      (ele) => ele?.remarks != ""
    );
    setIsAddRemarks({ ...isAddRemarks, remarks: remarks?.remarks });
    setBasePrice(data?.booking?.basePrice);
  }, [data]);

  useEffect(() => {
    setIsPaymentOffline((prev) => {
      return {
        ...prev,
        data: data?.booking?.offlinePaymentDetails,
      };
    });
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name == "paymentDate") {
      const isoFormattedDate = new Date(value).toISOString();
      setIsPaymentOffline((prevData) => ({
        ...prevData,
        data: {
          ...prevData.data,
          [name]: isoFormattedDate,
        },
      }));
    } else {
      setIsPaymentOffline((prevData) => ({
        ...prevData,
        data: {
          ...prevData.data,
          [name]: value,
        },
      }));
    }
  };

  const onToggleRemarksState = (state) => {
    setIsAddRemarks((prev) => {
      return {
        ...prev,
        state: state,
      };
    });
  };


  return (
    <>
      <div className="rounded-lg p-6 min-w-[460px] max-w-[460px]  mx-auto bg-[#EDEDEDED]">
        <div className="space-y-4 mb-4 p-3 bg-white rounded-lg shadow-md max-w-full">
          <div className="flex justify-between items-center border-b pb-2">
            <span className="font-semibold text-gray-800 text-sm">
              {t("usedCars.bookingSteps.name")}
            </span>
            <span className="text-gray-600 text-sm">
              {data?.buyerDetail?.firstName + " " + data?.buyerDetail?.lastName}
            </span>
          </div>

          <div className="flex justify-between items-center border-b pb-3">
            <span className="font-semibold text-gray-800 text-sm">
              {t("usedCars.bookingSteps.email")}
            </span>
            <span className="text-gray-600 text-sm">
              {data?.buyerDetail?.email}
            </span>
          </div>

          <div className="flex justify-between items-center border-b pb-3">
            <span className="font-semibold text-gray-800 text-sm">
              {t("usedCars.bookingSteps.phone")}
            </span>
            <span className="text-gray-600 text-sm">
              {data?.buyerDetail?.phone}
            </span>
          </div>

          <div className="flex justify-between items-center border-b pb-3">
            <span className="font-semibold text-gray-800 text-sm">
              {t("usedCars.bookingSteps.model")}
            </span>
            <span className="text-gray-600 text-sm">
              {data?.carDetail?.overview?.carName}
            </span>
          </div>

          <div className="flex justify-between items-center border-b pb-3">
            <span className="font-semibold text-gray-800 text-sm">
              {t("usedCars.bookingSteps.price")}
            </span>
            <span className="text-gray-600 text-sm">
              {`${country?.symbol ?? "$"} ${data?.carDetail?.overview?.price}`}
            </span>
          </div>

          <div className="flex justify-between items-center border-b pb-3">
            <span className="font-semibold text-gray-800 text-sm">
              {t("usedCars.bookingSteps.appointmentDate")}
            </span>
            <span className="text-gray-600 text-sm">
              {new Date(data?.booking?.testDrive?.date).toDateString()}
            </span>
          </div>

          <div className="flex justify-between items-center border-b pb-3">
            <span className="font-semibold text-gray-800 text-sm">
              {t("usedCars.bookingSteps.appointmentTime")}
            </span>
            <span className="text-gray-600 text-sm">
              {data?.booking?.testDrive?.timeSlot}
            </span>
          </div>

          {data?.booking?.testDrive?.proposedDate &&
            data?.booking?.testDrive?.status != "cancelled" ? (
            <div className="flex justify-between items-center border-b pb-3">
              <span className="font-semibold text-gray-800 text-sm">
                {t("bookServices.serviceBookingCard.fields.proposedDate")}
              </span>
              <span className="text-gray-600 text-sm">
                {new Date(
                  data?.booking?.testDrive?.proposedDate
                ).toDateString()}
              </span>
            </div>
          ) : null}

          {data?.booking?.testDrive?.proposedTime &&
            data?.booking?.testDrive?.status != "cancelled" ? (
            <div className="flex justify-between items-center border-b pb-3">
              <span className="font-semibold text-gray-800 text-sm">
                {t("bookServices.serviceBookingCard.fields.proposedTime")}
              </span>
              <span className="text-gray-600 text-sm">
                {data?.booking?.testDrive?.proposedTime}
              </span>
            </div>
          ) : null}
          {data?.booking?.testDrive?.proposedDate ? (
            <div className="flex justify-between items-center border-b pb-3">
              <span className="font-semibold text-gray-800 text-sm">
                {t("bookServices.serviceBookingCard.fields.proposedStatus")}:
              </span>
              <span className="text-gray-600 text-sm flex items-center gap-2">
                {data?.booking?.testDrive?.status === "rescheduleConfirmed" ? (
                  <span className="text-green-600">
                    {t("bookServices.closeCard.status.confirmed")}
                  </span>
                ) : data?.booking?.testDrive?.status === "cancelled" ? (
                  <span className="text-yellow-600">
                    {t("bookServices.closeCard.status.rejected")}
                  </span>
                ) : (
                  <span className="text-yellow-600">
                    {t("usedCars.table.pending")}
                  </span>
                )}
              </span>
            </div>
          ) : null}
          <div className="flex justify-between items-center border-b pb-3">
            <span className="font-semibold text-gray-800 text-sm">
              {t("usedCars.bookingSteps.paymentStatus")}
            </span>
            <span className="text-gray-600 text-sm">
              {t(`usedCars.bookingSteps.${data?.booking?.paymentStatus}`)}
         
              {/* {data?.booking?.paymentDetails?.totalAmount > 0
                ? ` $${data.booking.paymentDetails.totalAmount} Online`
                : data?.booking?.offlinePaymentDetails?.amountPaid > 0
                  ? ` $${data.booking.offlinePaymentDetails.amountPaid} Offline`
                  : ""} */}
            </span>
          </div>

          {currentStepInfo == 0 &&
            compareTwoDateIFG(
              proposedDate ? proposedDate : data?.booking?.testDrive?.date,
              new Date()
            ) &&
            data?.booking?.paymentStatus === "pending" &&
            ["pending", "accepted"].includes(data?.booking?.status) && (
              <div className="flex gap-1 justify-center items-center min-w-full">

                <RescheduleDatePicker
                  availableWorkingDays={availableWorkingDays}
                  date={data?.booking?.testDrive.date}
                  rescheduleDate={proposedDate}
                  setRescheduleDate={setProposedDate}
                  timeSlots={data?.booking?.testDrive.timeSlot}
                  proposedDate={data?.booking?.testDrive?.proposedDate}
                  rescheduleTime={proposedTime}
                  setRescheduleTime={setProposedTime}

                />

                {/* {proposedDate ? (
                  <select
                    value={proposedTime}
                    onChange={(e) => {
                      setProposedTime(e.target.value);
                    }}
                    className="w-28 border border-gray-300 rounded mt-2 h-12 mb-1"
                  >
                    <option selected value="">
                      ---
                    </option>
                    {availableWorkingDays
                      ?.find(({ date }) => compareTwoDate(date, proposedDate))
                      ?.timeSlots.map(({ time }, key) => (
                        <option key={key} value={time}>
                          {time}
                        </option>
                      ))}
                  </select>
                ) : null} */}
                <PrimaryButton
                  type="button"
                  onClick={async () => {
                    if (!proposedDate) {
                      toast.warn(t("serviceSettings.dateNotAvailable"));
                      return;
                    }
                    try {
                      if (data?._id) {
                        const body = {
                          _id: data._id,
                          proposedDate,
                          status: "rescheduleProposed",
                        };
                        if (proposedTime) {
                          body.proposedTime = proposedTime;
                        }

                        await onUpdate("rescheduleProposed", body);
                      }
                    } catch (error) {
                      console.log(error);
                    }
                  }}
                  disabled={loading}
                  loading={loading}
                  title={t(
                    "bookServices.serviceBookingCard.buttons.reschedule"
                  )}
                />
              </div>
            )}
          {(currentStepInfo == 2 || currentStepInfo == 1) && (
            <div className="flex justify-between items-center border-b pb-3">
              <span className="font-semibold text-gray-800 text-sm">
                {t("usedCars.bookingSteps.remarks")}
              </span>
              <span
                onClick={() =>
                  setIsAddRemarks({ ...isAddRemarks, isActive: true })
                }
                className="text-blue-600 text-sm cursor-pointer underline"
              >
                {t("usedCars.bookingSteps.addViewRemarks")}
              </span>

              {isAddRemarks.isActive && (
                <>
                  <Dialog
                    open={isAddRemarks.isActive}
                    onClose={() =>
                      setIsAddRemarks({ ...isAddRemarks, isActive: false })
                    }
                    className="relative z-50"
                  >
                    <div className="fixed inset-0 bg-black/70 flex w-screen items-center justify-center p-4">
                      <Dialog.Panel className="w-full flex justify-center items-center rounded">
                        <div className="max-h-[40vh] w-[400px] rounded-md relative bg-[#F5F5F5] p-4">
                          <div className="p-2">
                            <img
                              src={icons8crossFilled}
                              onClick={() =>
                                setIsAddRemarks({
                                  ...isAddRemarks,
                                  isActive: false,
                                })
                              }
                              className="w-8 h-8 absolute right-2 top-2 cursor-pointer"
                              alt="Close"
                            />
                            <div className="flex gap-8 p-1">
                              <p
                                onClick={() =>
                                  onToggleRemarksState("addRemarks")
                                }
                                className={`cursor-pointer ${isAddRemarks?.state == "addRemarks" &&
                                  "text-[#990000] pb-2 underline underline-offset-8 decoration-2 decoration-[#990000]"
                                  }`}
                              >
                                {t("usedCars.bookingSteps.addRemark")}
                              </p>
                              <p
                                onClick={() =>
                                  onToggleRemarksState("viewRemarks")
                                }
                                className={`cursor-pointer ${isAddRemarks?.state == "viewRemarks" &&
                                  "text-[#990000] pb-2 underline underline-offset-8 decoration-2 decoration-[#990000]"
                                  }`}
                              >
                                {t("usedCars.bookingSteps.viewRemark")}
                              </p>
                            </div>
                          </div>
                          <div className="mt-0 p-4">
                            <div className="flex justify-center items-center min-w-full">
                              {isAddRemarks?.state == "addRemarks" ? (
                                <div>
                                  <textarea
                                    type="text"
                                    name="remarks"
                                    id="remarks"
                                    value={isAddRemarks?.remarks}
                                    onChange={(e) =>
                                      setIsAddRemarks({
                                        ...isAddRemarks,
                                        remarks: e.target.value,
                                      })
                                    }
                                    placeholder={t(
                                      "usedCars.bookingSteps.enterRemarks"
                                    )}
                                    className="w-full border border-gray-300 rounded mt-2 mb-4"
                                  />
                                  <PrimaryButton
                                    type="button"
                                    onClick={() =>
                                      onUpdate(
                                        {
                                          remarks: isAddRemarks.remarks,
                                        },
                                        {
                                          _id: data?.booking?._id,
                                        }
                                      ).then(() => {
                                        setIsAddRemarks({
                                          ...isAddRemarks,
                                          isActive: false,
                                        });
                                      })
                                    }
                                    disabled={loading}
                                    loading={loading}
                                    className={`${loading ? "bg-gray-500" : "bg-[#990000]"
                                      } hover:bg-[#990000] text-white font-semibold w-full`}
                                    title={t(
                                      "usedCars.bookingSteps.buttons.addRemark"
                                    )}
                                  />
                                </div>
                              ) : (
                                <div className="p-0 text-start min-h-[130px]">
                                  <p className="text-start">
                                    {t("usedCars.bookingSteps.remarks")}{" "}
                                    {isAddRemarks?.remarks}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </Dialog.Panel>
                    </div>
                  </Dialog>
                </>
              )}
            </div>
          )}

          <div className="text-center mt-4">
            <p className="text-gray-700 italic text-sm">{description?.data}</p>
          </div>

          {current == 3 && (
            <>
              {data?.currentStatus == "dealClosed" ? (
                <h1 className="text-center font-medium">
                  {t("usedCars.bookingSteps.messages.dealAlreadyClosed")}
                </h1>
              ) : (
                <>
                  <div className="flex justify-center space-x-4">
                    <PrimaryButton
                      onClick={() => handleUpdate({ status: "dealClosed" })}
                      title={t("usedCars.bookingSteps.buttons.closeDeal")}
                      loading={loading}
                      disabled={loading}
                      className="w-36"
                    />

                    <button
                      onClick={() => setCurrentStepInfo()}
                      className="w-36 bg-transparent border border-gray-700 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                    >
                      {t("usedCars.bookingSteps.buttons.cancel")}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {current == 2 && (
            <>
              <div className="flex justify-center space-x-4">
                {data?.currentStatus == "reserved" ||
                  data?.currentStatus == "dealClosed" ? (
                  <h1 className="font-medium">
                    {t("usedCars.bookingSteps.alreadyReserved")}
                  </h1>
                ) : (
                  <>
                    <PrimaryButton
                      onClick={() => handleUpdate({ status: "reserved" })}
                      title={t("usedCars.bookingSteps.buttons.reserve")}
                      loading={
                        buttonLoading?.status &&
                        buttonLoading?.hasOwnProperty("reserved")
                      }
                      disabled={loading}
                    />

                    {/* <PrimaryButton
                      onClick={() => setIsReschedule(!isReschedule)}
                      title={t("usedCars.bookingSteps.buttons.proposeNewDate")}
                    /> */}
                  </>
                )}
              </div>
            </>
          )}

          {current == 5 && (
            <>
              <PrimaryButton
                type="button"
                onClick={() =>
                  handleUpdate({
                    checkPaymentStatus: true,
                  })
                }
                disabled={loading}
                loading={loading}
                className={`${loading ? "bg-gray-500" : "bg-[#990000]"
                  } hover:bg-[#990000] text-white font-semibold w-full`}
                title={t("usedCars.bookingSteps.buttons.checkPaymentStatus")}
              />
            </>
          )}

          {current == 6 && (
            <div className="flex flex-col gap-2 items-center">
              <PrimaryButton
                type="button"
                onClick={() =>
                  handleUpdate({
                    checkPaymentStatus: true,
                  })
                }
                disabled={
                  buttonLoading?.status &&
                  buttonLoading?.hasOwnProperty("checkPaymentStatus")
                }
                loading={
                  buttonLoading?.status &&
                  buttonLoading?.hasOwnProperty("checkPaymentStatus")
                }
                className={`${loading ? "bg-gray-500" : "bg-[#990000]"
                  } hover:bg-[#990000] text-white w-80 h-18`}
                title={t("usedCars.bookingSteps.buttons.checkStatus")}
              />
              <PrimaryButton
                onClick={() =>
                  handleUpdate(
                    {
                      generatePaymentLink: {
                        amount: +data?.carDetail?.overview?.price,
                      },
                    },
                    null
                  )
                }
                title={t("usedCars.bookingSteps.buttons.sendPaymentRequest")}
                loading={
                  buttonLoading?.status &&
                  buttonLoading?.hasOwnProperty("generatePaymentLink")
                }
                className={`${loading ? "bg-gray-500" : "bg-[#990000]"
                  } hover:bg-[#990000] text-white w-80 h-18`}
                disabled={
                  buttonLoading?.status &&
                  buttonLoading?.hasOwnProperty("generatePaymentLink")
                }
              />

              <PrimaryButton
                onClick={() =>
                  setIsPaymentOffline((prev) => {
                    return { ...prev, isActive: true };
                  })
                }
                title={t("usedCars.bookingSteps.buttons.payByCash")}
                className={`${loading ? "bg-gray-500" : "bg-[#990000]"
                  } hover:bg-[#990000] text-white w-80 h-18`}
                disabled={loading}
              />
            </div>
          )}

          {current == 4 && data?.booking?.status != "rejected" && (
            <>
              <div className="flex justify-center space-x-4">
                <div className="w-full">
                  <textarea
                    value={data?.cancellationReason}
                    onChange={(e) =>
                      setData({ ...data, cancellationReason: e.target.value })
                    }
                    placeholder={t(
                      "usedCars.bookingSteps.placeholders.rejectionReason"
                    )}
                    className="w-full border border-gray-300 rounded mt-2"
                    rows="4"
                  />

                  <PrimaryButton
                    type="button"
                    onClick={() =>
                      handleUpdate({
                        status: "rejected",
                        cancellationReason: data?.cancellationReason,
                      })
                    }
                    disabled={loading}
                    loading={loading}
                    className={`${loading ? "bg-gray-500" : "bg-[#990000]"
                      } hover:bg-[#990000] text-white font-semibold w-full`}
                    title={t("usedCars.bookingSteps.buttons.submit")}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {isPaymentOffline && (
          <Dialog
            open={isPaymentOffline.isActive}
            onClose={() =>
              setIsPaymentOffline((prev) => {
                return { ...prev, isActive: false };
              })
            }
            className="relative z-50"
          >
            <div className="fixed inset-0 bg-black/70 flex w-screen items-center justify-center p-4">
              <Dialog.Panel className="w-full flex justify-center items-center rounded">
                <div className="max-h-[60vh] w-[400px] rounded-md relative bg-[#F5F5F5] p-4">
                  <div className="p-2">
                    <img
                      src={icons8crossFilled}
                      onClick={() =>
                        setIsPaymentOffline((prev) => {
                          return { ...prev, isActive: false };
                        })
                      }
                      className="w-8 h-8 absolute right-2 top-2 cursor-pointer"
                      alt="Close"
                    />
                  </div>

                  <div className="mt-0 p-4">
                    <p className="p-1 pb-4 text-center m-auto font-semibold">
                      {t("usedCars.bookingSteps.offlinePaymentDetails")}
                    </p>

                    <div className="justify-center items-center min-w-full">
                      <form className="space-y-4 mb-6">
                        <div>
                          <label className="block mb-1 font-medium">
                            {t("usedCars.bookingSteps.paymentMethod")}
                          </label>
                          <select
                            name="paymentMethod"
                            value={isPaymentOffline?.data?.paymentMethod}
                            onChange={handleInputChange}
                            className="border p-2 rounded-md w-full"
                          >
                            <option value="">
                              {t("usedCars.bookingSteps.selectMethod")}
                            </option>
                            <option value="cash">
                              {t("usedCars.bookingSteps.options.cash")}
                            </option>
                            <option value="card">
                              {t("usedCars.bookingSteps.options.card")}
                            </option>
                            <option value="bankTransfer">
                              {t("usedCars.bookingSteps.options.bankTransfer")}
                            </option>
                          </select>
                        </div>

                        <div>
                          <label className="block mb-1 font-medium">
                            {t("usedCars.bookingSteps.paymentDate")}
                          </label>
                          <input
                            type="date"
                            name="paymentDate"
                            value={
                              isPaymentOffline?.data?.paymentDate?.split(
                                "T"
                              )[0] || ""
                            }
                            onChange={handleInputChange}
                            className="border p-2 rounded-md w-full"
                            required
                          />
                        </div>

                        <div className="mb-4">
                          <label className="block mb-1 font-medium">
                            {t("usedCars.bookingSteps.basePrice")}
                          </label>
                          <input
                            type="number"
                            value={basePrice}
                            onChange={handleBasePriceChange}
                            className="border p-2 rounded-md w-full"
                            placeholder={t(
                              "usedCars.bookingSteps.placeholders.basePrice"
                            )}
                            required
                            min={0}
                            step={1}
                            onKeyDown={(e) => {
                              if (e.code === "Minus") {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>

                        <div>
                          <label className="block mb-1 font-medium">
                            {t("usedCars.bookingSteps.amountPaid")}
                          </label>
                          <input
                            type="number"
                            name="amountPaid"
                            value={isPaymentOffline?.data?.amountPaid}
                            onChange={handleInputChange}
                            className="border p-2 rounded-md w-full"
                            placeholder={t(
                              "usedCars.bookingSteps.placeholders.amountPaid"
                            )}
                            required={true}
                            min={0}
                            step={1}
                            onKeyDown={(e) => {
                              if (e.code === "Minus") {
                                e.preventDefault();
                              }
                            }}
                          />
                        </div>
                      </form>

                      <Button
                        type="submit"
                        onClick={() => {
                          if (
                            !isPaymentOffline?.data?.paymentMethod ||
                            !isPaymentOffline?.data?.paymentDate ||
                            !isPaymentOffline?.data?.amountPaid
                          ) {
                            notifyWarning(
                              t(
                                "usedCars.bookingSteps.messages.pleaseFillAllFields"
                              )
                            );
                            return;
                          }

                          setIsPaymentOffline((prevData) => {
                            return {
                              ...prevData,
                              data: {
                                ...prevData.data,
                                basePrice: basePrice,
                              },
                            };
                          });

                          handleUpdate({
                            offlinePaymentConfirmed: true,
                            offlinePaymentDetails: isPaymentOffline.data,
                          });
                        }}
                        disabled={loading}
                        className={`${loading ? "bg-gray-500" : "bg-[#990000]"
                          } hover:bg-[#990000] text-white font-semibold h-11 w-full`}
                        title={t("usedCars.bookingSteps.buttons.submit")}
                      >
                        {loading ? (
                          <AiOutlineLoading className="h-6 w-6 animate-spin m-auto" />
                        ) : (
                          t("usedCars.bookingSteps.buttons.submit")
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </div>
          </Dialog>
        )}
      </div>
    </>
  );
};
